import { PaymentMethods } from "@/types/db/accounting";
import { LogService } from "@/services/logging";
import { DiscountType } from "@smeetz/order-common";
import { useOrderStore } from "@smeetz/order-store";
import { CheckPromoCodeDeclineReason } from "@smeetz/discount-common";
import {
  errorChain,
  orderErrorCode,
} from "@/utils/vue-helpers/order-action-errors.helper";
import POSConfigModule from "@/store/modules/pos-config";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import { getPromocodeCampaginsByConfigId } from "@/api/gql/promocode-campaign";
import { IPromocodeCampaign } from "@/types/db/promocode-campaign";
import { getSmeetzPictures } from "@/api/gql/common";
import { dexieDB } from "@/services/dexie-database";
import { atomicTableSwap } from "@/dexie/helpers";
import {
  createCroppedImageproxyUrl,
  SmeetzPictureEntityTables,
  SmeetzPictureEntityTypes,
} from "../helpers/pictures";
import dayjs from "dayjs";

/**
 * This function extracts the voucher code from a url string
 *
 * Examples of possible url strings we get from the scanner:
 * https//appsmeetz.com/redeemvouchers?vc=UY4W2K3M3T
 * https//appsmeetzcom/redeemvouchersvc=UY4W2K3M3T
 * https//appsmeetzcom/redeemvouchersvcUY4W2K3M3T
 * https://app.smeetz.com/redeem-vouchers?vc=572ZS64YLH_old_236503
 *
 */
export function extractCodeFromUrl(value: string): string {
  const promoCodeMatch = value.match(/vc=?([A-Za-z0-9_-]+)/);
  if (promoCodeMatch) {
    return promoCodeMatch[1];
  }
  return "";
}

export function getPromocodeInputPlaceholder(type: PaymentMethods): string {
  if (type === PaymentMethods.POS_GIFT_CARD) {
    return t("promocode.enter-gift-card");
  }
  if (type === PaymentMethods.POS_MEMBERSHIP) {
    return t("promocode.enter-membership");
  }
  return t("promocode.enter");
}

export function getPromocodeSuccessMessage(type: PaymentMethods): string {
  if (type === PaymentMethods.POS_GIFT_CARD) {
    return t("promocode.gift-card-applied");
  }
  if (type === PaymentMethods.POS_MEMBERSHIP) {
    return t("promocode.membership-applied");
  }
  return t("promocode.promo-code-applied");
}

/** Code tab matching the instrument an applied code landed on. */
export function getAppliedCodePaymentType(
  code: string
): PaymentMethods | undefined {
  const { orderDiscounts, vouchers } = useOrderStore().selectedOrderSummary;
  const discount = orderDiscounts.find((od) => od.code === code);
  if (discount?.type === DiscountType.MEMBERSHIP_VOUCHER) {
    return PaymentMethods.POS_MEMBERSHIP;
  }
  if (
    discount?.type === DiscountType.VOUCHER ||
    vouchers.some((voucher) => voucher.code === code)
  ) {
    return PaymentMethods.POS_GIFT_CARD;
  }
  return discount ? PaymentMethods.POS_PROMO_CODE : undefined;
}

const DISCOUNT_CODE_ERROR_KEYS: Record<string, string> = {
  NETWORK_ERROR: "couldnt-process",
  PROMO_CODE_NOT_APPLICABLE: "promo-code-not-valid",
  PROMO_CODE_ALREADY_APPLIED: "already-applied",
  VOUCHER_FULLY_CONSUMED: "gift-card-not-available",
  ORDER_ALREADY_COVERED: "order-already-covered",
  EXTERNAL_VOUCHER_INVALID: "gift-card-not-valid",
};

const CHECK_PROMO_CODE_ERROR_KEYS: Partial<
  Record<CheckPromoCodeDeclineReason, string>
> = {
  [CheckPromoCodeDeclineReason.NOT_FOUND]: "promo-code-not-found",
  [CheckPromoCodeDeclineReason.EXPIRED]: "promo-code-expired",
  [CheckPromoCodeDeclineReason.REDEEMED]: "promo-code-not-available",
  [CheckPromoCodeDeclineReason.NOT_AVAILABLE]: "promo-code-not-available",
  [CheckPromoCodeDeclineReason.INVALID_DAYS]: "promo-code-not-valid",
};

/**
 * Translation key for one link of the error chain, undefined when it is not
 * a known decline. The only place package codes meet POS wording, to be
 * dropped once the packages ship their own messages.
 */
function resolveDiscountCodeErrorKey(link: unknown): string | undefined {
  const { name, reason, statusCode } = link as {
    name?: string;
    reason?: CheckPromoCodeDeclineReason;
    statusCode?: number;
  };
  if (name === "CheckPromoCodeApiError") {
    return reason ? CHECK_PROMO_CODE_ERROR_KEYS[reason] : undefined;
  }
  if (name === "ExternalVoucherApiError") {
    return statusCode && statusCode < 500 ? "gift-card-not-valid" : undefined;
  }
  return DISCOUNT_CODE_ERROR_KEYS[orderErrorCode(link) ?? ""];
}

/** Cashier-facing message for a failed code apply. */
export function getDiscountCodeErrorMessage(error: unknown): string {
  for (const link of errorChain(error)) {
    const key = resolveDiscountCodeErrorKey(link);
    if (key) {
      return t(`promocode.${key}`);
    }
  }
  return t("error.general-error");
}

/**
 * Pull and store promocodeCampaigns linked to the selected POS config
 */
export async function pullAndStorePromocodeCampaigns(signal?: AbortSignal) {
  const posConfigId = POSConfigModule.selectedConfig?.id;
  if (!posConfigId) {
    return;
  }

  LogService.info(
    `Pulling and storing promocode campaigns linked to the selected POS config: ${posConfigId}`
  );

  const allCampaigns: IPromocodeCampaign[] = [];
  const limit = 100;
  let skip = 0;
  // eslint-disable-next-line
	while (true) {
    if (signal?.aborted) break;
    const campaings = await getPromocodeCampaginsByConfigId(posConfigId, {
      skip,
      limit,
    });
    if (!campaings || !campaings.length) {
      break;
    }
    // pull the pictures of the campaigns
    const ids = campaings
      .filter((c) => c?.PromoCodeCampaignRead?.id)
      .map((c) => Number(c?.PromoCodeCampaignRead?.id));
    const pictures = await getSmeetzPictures(
      SmeetzPictureEntityTypes.PromoCode,
      ids
    );

    skip += campaings.length;

    const now = dayjs();
    for (const campaign of campaings) {
      const c = campaign?.PromoCodeCampaignRead;
      const endDateTime = c?.endDateTime;
      if (!c || c.cancelled === 1 || dayjs(endDateTime).isBefore(now)) continue;
      const campaignPicture = pictures.find((p) => p?.entityId === c.id);
      const picture = campaignPicture
        ? createCroppedImageproxyUrl({
            name: campaignPicture?.name,
            table: SmeetzPictureEntityTables.PromoCode,
            metaData: campaignPicture?.metaData,
          })
        : "";
      allCampaigns.push({
        id: Number(c?.id),
        posConfigId: Number(campaign?.posConfigId),
        name: String(c?.name),
        startDateTime: String(c?.startDateTime),
        endDateTime: String(c?.endDateTime),
        codes: c?.codes?.map((p) => String(p?.code || p?.codeUnique)) || [],
        isPinned: Boolean(campaign?.pinned),
        flatValue: Number(c?.flatValue),
        percentageValue: Number(c?.percentValue),
        picture: picture,
        position: campaign?.position ?? 25,
      });
    }
  }

  // Atomically replace the promocode campaigns table in Dexie
  // If this fails, the old data remains intact
  if (!signal?.aborted) {
    await atomicTableSwap(dexieDB.promocodeCampaigns as any, allCampaigns);
  }

  LogService.info(
    `Done pulling and storing promocode campaigns linked to the selected POS config: ${posConfigId}`
  );
}
